import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) nav("/espace-client");
  }, [user, nav]);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    const res = await register({
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim() || null,
      password: form.password,
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Compte créé avec succès. Bienvenue !");
      nav("/espace-client");
    } else {
      setError(res.error);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-teal-dark grain overflow-hidden">
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-l3 hover:text-white text-sm" data-testid="register-back-home">
            <ArrowLeft size={16} /> Retour au site
          </Link>
          <div>
            <p className="eyebrow text-teal-l3">Espace client</p>
            <h1 className="mt-4 font-serif text-5xl leading-[1.02] font-light">
              Créez votre <em className="text-teal-l3">compte</em>,<br />
              accédez à vos publications.
            </h1>
            <p className="mt-6 text-teal-l3 max-w-md">
              En quelques secondes, rejoignez l'espace privé du cabinet pour retrouver
              nos notes fiscales, actualités et documents partagés.
            </p>
          </div>
          <div className="text-xs text-teal-l3">© 2026 Ducrot &amp; Associés</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="register-form" className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-teal-dark text-sm mb-6">
            <ArrowLeft size={16} /> Retour
          </Link>
          <p className="eyebrow text-teal-accent">Inscription</p>
          <h2 className="mt-3 font-serif text-4xl text-teal-dark">Créer un compte</h2>
          <p className="mt-3 text-ink-500 text-sm">
            Vous serez automatiquement connecté après la création.
          </p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Nom complet *</span>
              <input
                data-testid="register-name"
                required minLength={2}
                value={form.name} onChange={change("name")}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Adresse email *</span>
              <input
                data-testid="register-email"
                type="email" required
                value={form.email} onChange={change("email")}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Entreprise (facultatif)</span>
              <input
                data-testid="register-company"
                value={form.company} onChange={change("company")}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Mot de passe * (8 caractères min.)</span>
              <input
                data-testid="register-password"
                type="password" required minLength={8}
                value={form.password} onChange={change("password")}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Confirmer le mot de passe *</span>
              <input
                data-testid="register-confirm"
                type="password" required minLength={8}
                value={form.confirm} onChange={change("confirm")}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
          </div>

          {error && (
            <div data-testid="register-error" className="mt-4 border-l-4 border-red-500 bg-red-50 text-red-800 text-sm px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="register-submit"
            disabled={loading}
            className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
          >
            {loading ? "Création…" : (<>Créer mon compte <ArrowUpRight size={16} /></>)}
          </button>

          <p className="mt-8 text-sm text-ink-500">
            Déjà un compte ?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-teal-dark underline hover:text-teal-accent">
              Se connecter
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
